import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Row,
  Col,
  Badge,
  Form,
  OverlayTrigger,
  Tooltip,
  Offcanvas,
} from "react-bootstrap";
import { FiInfo, FiSliders } from "react-icons/fi";
import {
  Calendar,
  Search,
  Box,
  Truck,
  CheckCircle,
  Clock,
  FileText,
  Layers,
  X,
  ArrowUpDown,
  Pencil,
  Filter,
  User,
  Building,
} from "lucide-react";

const STATUS_HIERARCHY = {
  ORDINATO: 1,
  RIMESSO: 2,
  "ENTRATO IN MAGAZZINO": 3,
  "BEM INVIATA": 4,
  "BEM COMPLETATA": 5,
  "INVIO ODP/ODPV": 6,
  "COMPLETAMENTO ODP/ODPV": 7,
  EVASO: 8,
};

// NOTA: nome campo prezzo fornitore da confermare - al momento nel JSON fornito
// esiste solo "Prezzo Netto" (prezzo cliente). Cambia questa costante se il campo
// nel tuo dataset reale si chiama diversamente (es. "Prezzo Netto Fornitore",
// "Prezzo Acquisto", ecc.)
const SUPPLIER_PRICE_FIELD = "Prezzo Netto for";

const renderTimelineStep = (
  label,
  dateString,
  infoAggiuntiva = "",
  isCurrentStatus = false,
) => {
  const isDone =
    dateString &&
    dateString !== "null" &&
    dateString !== null &&
    dateString !== "(NULL)";
  return (
    <Col className="text-center px-1">
      <div
        style={{
          fontSize: "0.6rem",
          fontWeight: isCurrentStatus ? "900" : "800",
          color: isCurrentStatus ? "#dc3545" : isDone ? "#0d6efd" : "#adb5bd",
          textTransform: "uppercase",
          marginBottom: "4px",
          minHeight: "24px",
        }}
      >
        {label}
      </div>
      {infoAggiuntiva && (
        <div
          style={{ fontSize: "0.55rem", color: "#6c757d", minHeight: "14px" }}
        >
          {isDone ? infoAggiuntiva : ""}
        </div>
      )}
      <div
        style={{
          height: "4px",
          backgroundColor: isCurrentStatus
            ? "#dc3545"
            : isDone
              ? "#0d6efd"
              : "#e9ecef",
          borderRadius: "2px",
          marginTop: "8px",
          marginBottom: "8px",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-5px",
            left: "50%",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: isCurrentStatus
              ? "#dc3545"
              : isDone
                ? "#0d6efd"
                : "#dee2e6",
            transform: "translateX(-50%)",
            border: isCurrentStatus ? "2px solid white" : "none",
            boxShadow: isCurrentStatus
              ? "0 0 8px rgba(220, 53, 69, 0.6)"
              : "none",
          }}
        ></div>
      </div>
      <div
        style={{
          fontSize: "0.65rem",
          color: isCurrentStatus ? "#dc3545" : isDone ? "#212529" : "#dee2e6",
          fontWeight: isDone ? "600" : "400",
        }}
      >
        {isDone
          ? new Date(dateString.replace(/-/g, "/")).toLocaleDateString("it-IT")
          : "--"}
      </div>
    </Col>
  );
};

const calculateStatus = (order) => {
  if (isFullyEvaded(order)) return "EVASO";
  if (order["Data Completamento ODPV"]) return "COMPLETAMENTO ODP/ODPV";
  if (order["Data Invio ODPV"]) return "INVIO ODPV";
  if (order["bem_data_copletamento"]) return "BEM COMPLETATA";
  if (order["bem_data_invio"] || order["id_bem"]) return "BEM INVIATA";
  if (order["data_entrata"] || order["numero_entrata"])
    return "ENTRATO IN MAGAZZINO";
  if (order["Data Rimesso"] || order["numero_rimesso"]) return "RIMESSO";
  return "ORDINATO";
};

// Helper: valida se la causale e' effettivamente valorizzata (non null/(NULL)/vuota)
const hasCausale = (order) => {
  const v = order?.["causale"];
  return v !== null && v !== undefined && v !== "(NULL)" && v !== "";
};

// Helper: valida se un valore "Num Ord Cli" e' effettivamente valorizzato
const hasClientOrder = (order) => {
  const v = order?.["Num Ord Cli"];
  return !!v && v !== "(NULL)" && v !== "null";
};

// TODO: conferma nomi campi reali nel JSON
const QTY_FIELDS = {
  // CLIENTE
  cliOrdinata: "Quantita cli",
  cliEvasa: "Qta EVA cli",
  cliDev: "Qta DEV cli",

  // FORNITORE
  forOrdinata: "Quantita",
  forRimessa: "Qta RIM",
  forEvasa: "Qta EVA",
  forDev: "Qta DEV",
};

const isFullyEvaded = (order) => {
  const qtaOrdCli = parseFloat(order?.[QTY_FIELDS.cliOrdinata] ?? 0);
  const qtaEvaCli = parseFloat(order?.[QTY_FIELDS.cliEvasa] ?? 0);
  const qtaOrdFor = parseFloat(order?.[QTY_FIELDS.forOrdinata] ?? 0);
  const qtaEvaFor = parseFloat(order?.[QTY_FIELDS.forEvasa] ?? 0);

  const cliEvaso = qtaOrdCli > 0 && qtaEvaCli >= qtaOrdCli;
  const forEvaso = qtaOrdFor > 0 && qtaEvaFor >= qtaOrdFor;

  return cliEvaso && forEvaso;
};

// Helper: chiave di raggruppamento righe.
// Ora la chiave combina SEMPRE ordine cliente + ordine fornitore:
// due righe finiscono nello stesso gruppo solo se hanno
// sia lo stesso Num Ord Cli, sia lo stesso ordine fornitore (Num Doc / ID Ordine FOR).
const getGroupKey = (order) => {
  const cliKey = hasClientOrder(order) ? order["Num Ord Cli"] : "NOCLI";

  const forKey =
    order?.["Num Doc"] ||
    order?.["ID Ordine FOR"] ||
    order?.["Articolo"] ||
    "N/D";

  return `CLI-${cliKey}__FOR-${forKey}`;
};

const renderDeliveryBadge = (order) => {
  const deliveryStr = order?.["Data Prev Consegna Cli"];

  if (!deliveryStr) {
    return (
      <span className="badge bg-secondary">
        —
      </span>
    );
  }

  const deliveryDate = new Date(deliveryStr);
  
  // Data di oggi senza ore
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Data consegna senza ore
  const deliveryDay = new Date(deliveryDate);
  deliveryDay.setHours(0, 0, 0, 0);

  const isOverdue = deliveryDay < today;

  return (
    <span
      className={`badge ${
        isOverdue ? "bg-danger" : "bg-success"
      }`}
    >
      {deliveryDate.toLocaleDateString("it-IT")}
    </span>
  );
};
// Helper: valida se una stringa data e' effettivamente valorizzata
const isValidDateStr = (d) => !!d && d !== "(NULL)" && d !== "null";

// Helper: restituisce la stringa data piu' recente tra due date (o null se nessuna valida)
const getMostRecentDateStr = (dateStr1, dateStr2) => {
  const v1 = isValidDateStr(dateStr1);
  const v2 = isValidDateStr(dateStr2);
  if (v1 && v2) {
    const d1 = new Date(dateStr1.split(" ")[0]);
    const d2 = new Date(dateStr2.split(" ")[0]);
    return d1 >= d2 ? dateStr1 : dateStr2;
  }
  if (v1) return dateStr1;
  if (v2) return dateStr2;
  return null;
};

const formatDateOrDash = (dateStr) => {
  if (!isValidDateStr(dateStr)) return "--";
  const d = new Date(dateStr.split(" ")[0]);
  return isNaN(d.getTime()) ? "--" : d.toLocaleDateString("it-IT");
};

// Helper: converte una stringa data nel timestamp del solo giorno (senza ora), o null se non valida
const parseDayTimestamp = (dStr) => {
  if (!isValidDateStr(dStr)) return null;
  const datePart = dStr.split(" ")[0].split("T")[0];
  const d = new Date(datePart.replace(/-/g, "/"));
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const getTodayTimestamp = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
};

// --- LOGICA SEMAFORO (Rosso, Giallo, Verde) ---
// --- STATO TEMPI: usa direttamente STATO ORDINE restituito dalla query SQL ---
const getTrafficLight = (order) => {
  // STATO ORDINE arriva direttamente dalla query SQL come
  // ROSSO / GIALLO / VERDE. Manteniamo anche i vecchi valori
  // DANGER / WARNING / OK per compatibilita con eventuali endpoint legacy.
  const stato = String(order?.["STATO ORDINE"] || "")
    .trim()
    .toUpperCase();

  switch (stato) {
    case "ROSSO":
    case "DANGER":
      return {
        color: "#dc3545",
        label: "DANGER",
        bg: "#ffe3e3",
        key: "ROSSO",
      };

    case "GIALLO":
    case "WARNING":
      return {
        color: "#f59f00",
        label: "WARNING",
        bg: "#fff9db",
        key: "GIALLO",
      };

    case "VERDE":
    case "OK":
      return {
        color: "#2b8a3e",
        label: "OK",
        bg: "#ebfbee",
        key: "VERDE",
      };

    default:
      return {
        color: "#6c757d",
        label: "N/D",
        bg: "#f1f3f5",
        key: "N/D",
      };
  }
};

const TRAFFIC_OPTIONS = [
  { key: "ROSSO", color: "#dc3545", bg: "#ffe3e3", label: "DANGER" },
  { key: "GIALLO", color: "#f59f00", bg: "#fff9db", label: "WARNING" },
  { key: "VERDE", color: "#2b8a3e", bg: "#ebfbee", label: "OK" },
];

const SalesChart = () => {
  const [urgentOrders, setUrgentOrders] = useState([]);
  const [entrateData, setEntrateData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // FILTRI
  const [showFiltersCanvas, setShowFiltersCanvas] = useState(false);
  const [statusFilter, setStatusFilter] = useState("TUTTI");

  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getDefaultDateRange = () => {
    const oggi = new Date();

    // ESATTAMENTE 180 GIORNI FA
    const dataInizio = new Date(oggi);
    dataInizio.setDate(dataInizio.getDate() - 180);

    return {
      start: getLocalDateString(dataInizio),
      end: getLocalDateString(oggi),
    };
  };

  // Per la chiamata PHP usiamo come limite tecnico il giorno successivo.
  // Questo evita di perdere gli ordini del giorno finale se il backend
  // confronta un timestamp con dataFine alle 00:00:00.
  // Il filtro React continua comunque a mostrare SOLO fino alla data
  // scelta dall'utente, quindi eventuali righe del giorno successivo
  // non vengono visualizzate.
  const addOneDayToDateString = (dateString) => {
    if (!dateString) return "";

    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + 1);

    return getLocalDateString(date);
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [onlyNoClient, setOnlyNoClient] = useState(false);
  const [hideNoClient, setHideNoClient] = useState(false);
  const [onlyWithCausale, setOnlyWithCausale] = useState(false);

  // EVASI PARZIALI: attivo di default.
  // Quando e' attivo nasconde SOLO le righe in cui sia il cliente
  // sia il fornitore hanno Qta DEV = 0.
  const [partialEvadedFilter, setPartialEvadedFilter] = useState(true);

  // Semaforo: ora multi-selezione, vive fuori dal pannello filtri, accanto alla ricerca.
  // Array vuoto = nessun filtro attivo (mostra tutti i colori).
  const [trafficFilters, setTrafficFilters] = useState([]);

  const [sortBy, setSortBy] = useState("DATE_DESC");
  const [showModal, setShowModal] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const currentStatus = selectedOrder
    ? selectedOrder.calculatedStatus || calculateStatus(selectedOrder)
    : "";

  const [editDates, setEditDates] = useState({});
  const [savingDates, setSavingDates] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const [inlineEdit, setInlineEdit] = useState({
    show: false,
    field: "",
    label: "",
    value: "",
  });

  const loadOrders = async (start, end) => {
    try {
      // dataFineFetch e' volutamente il giorno successivo per includere
      // tutto il giorno finale anche se il PHP lavora con timestamp.
      const dataFineFetch = end ? addOneDayToDateString(end) : end;

      console.log(
        "🔄 Caricamento ordini:",
        start,
        "→",
        end,
        "(limite backend:",
        dataFineFetch,
        ")",
      );

      const url =
        `https://dashboard.technoparts.it/ws/stato_ordini.php` +
        `?dataInizio=${encodeURIComponent(start)}` +
        `&dataFine=${encodeURIComponent(dataFineFetch)}` +
        `&_ts=${Date.now()}`;

      // no-store evita di riutilizzare una risposta GET vecchia:
      // gli ordini inseriti oggi devono comparire al nuovo caricamento.
      const res = await fetch(url, { cache: "no-store" });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const json = await res.json();

      console.log("📦 Risposta PHP:", json);

      const ordiniRaw = json?.STATO_ORDINI?.flat() || [];
      const entrateRaw = json?.ENTRATE || [];

      setEntrateData(entrateRaw);

      const mapped = ordiniRaw.map((order) => ({
        ...order,
        calculatedStatus: calculateStatus(order),
      }));

      console.log("📊 Ordini caricati:", mapped.length);

      setUrgentOrders(
        mapped.sort((a, b) => getGroupKey(a).localeCompare(getGroupKey(b))),
      );
    } catch (err) {
      console.error("❌ Errore nel caricamento dati:", err);
      setUrgentOrders([]);
    }
  };

  useEffect(() => {
    loadOrders(dateRange.start, dateRange.end);
  }, []);
  const handleOpenModal = async (ordine) => {
    try {
      const idOrdine = ordine["ID Ordine CLI"];

      const res = await fetch(
        `https://dashboard.technoparts.it/ws/test_ordini.php?id_ordine=${idOrdine}`,
      );

      const dettaglio = await res.json();

      console.log("RISPOSTA PHP:", dettaglio);

      if (dettaglio.success && dettaglio.data?.length > 0) {
        setSelectedOrder(dettaglio.data[0]);
      } else {
        setSelectedOrder(ordine);
      }

      setShowModal(true);
    } catch (error) {
      console.error("Errore caricamento dettaglio:", error);

      // fallback al dato della tabella
      setSelectedOrder(ordine);
      setShowModal(true);
    }
  };

  const toggleTrafficFilter = (key) => {
    setTrafficFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleSaveDates = async () => {
    if (!inlineEdit.field) return;

    setSavingDates(true);
    setSaveMsg("");

    const currentField = inlineEdit.field;
    const currentValue = inlineEdit.value;

    const payload = {
      articolo: selectedOrder["Articolo"],
      id_ordine_for: null,
      id_ordine_cli: null,
      data_conferma_fornitori: null,
      data_effettiva_fornitori: null,
      data_conferma_clienti: null,
      data_effettiva_clienti: null,
    };

    if (
      currentField === "data_conferma_fornitori" ||
      currentField === "data_effettiva_fornitori"
    ) {
      payload.id_ordine_for = selectedOrder["ID Ordine FOR"];
      payload[currentField] = currentValue;
    } else if (
      currentField === "data_conferma_clienti" ||
      currentField === "data_effettiva_clienti"
    ) {
      payload.id_ordine_cli = selectedOrder["ID Ordine CLI"];
      payload[currentField] = currentValue;
    }

    setInlineEdit({ show: false, field: "", label: "", value: "" });

    try {
      const res = await fetch(
        "https://dashboard.technoparts.it/ws/edit-data.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (data.success) {
        setSaveMsg("✅ Data salvata correttamente");

        setUrgentOrders((prev) =>
          prev.map((o) => {
            if (
              o["ID Ordine CLI"] === selectedOrder["ID Ordine CLI"] &&
              o["Articolo cli"] === selectedOrder["Articolo cli"]
            ) {
              const updatedOrder = { ...o };
              if (currentField === "data_conferma_fornitori")
                updatedOrder["Data Conferma Fornitori"] = currentValue;
              if (currentField === "data_effettiva_fornitori")
                updatedOrder["Data Effettiva Fornitori"] = currentValue;

              updatedOrder.calculatedStatus = calculateStatus(updatedOrder);
              return updatedOrder;
            }
            return o;
          }),
        );
      } else {
        setSaveMsg("❌ Errore: " + (data.error || "sconosciuto"));
      }
    } catch (e) {
      console.error("💥 ERRORE DI RETE:", e);
      setSaveMsg("❌ Errore di rete / Connessione fallita");
    } finally {
      setSavingDates(false);
    }
  };

  // Filtro "Evasi parziali".
  // Se disattivato non esclude nulla.
  // Se attivato esclude SOLO quando entrambe le quantita' da evadere
  // (cliente e fornitore) sono realmente valorizzate e uguali a zero.
  const matchesPartialEvadedFilter = (order) => {
    if (!partialEvadedFilter) return true;

    const qtaDevCli = parseFloat(order?.[QTY_FIELDS.cliDev]);
    const qtaDevFor = parseFloat(order?.[QTY_FIELDS.forDev]);

    const cliIsZero = Number.isFinite(qtaDevCli) && qtaDevCli === 0;
    const forIsZero = Number.isFinite(qtaDevFor) && qtaDevFor === 0;

    return !(cliIsZero && forIsZero);
  };

  const getStatusStyle = (stato) => {
    const map = {
      ORDINATO: { bg: "#f1f3f5", color: "#495057", icon: <Clock size={12} /> },
      RIMESSO: { bg: "#f3f0ff", color: "#6741d9", icon: <Layers size={12} /> },
      "ENTRATO IN MAGAZZINO": {
        bg: "#e3fafc",
        color: "#0c8599",
        icon: <Box size={12} />,
      },
      "BEM INVIATA": {
        bg: "#fff9db",
        color: "#f08000",
        icon: <FileText size={12} />,
      },
      "BEM COMPLETATA": {
        bg: "#fff0f6",
        color: "#d6336c",
        icon: <CheckCircle size={12} />,
      },
      "INVIO ODP/ODPV": {
        bg: "#f3f0ff",
        color: "#6741d9",
        icon: <Truck size={12} />,
      },
      "COMPLETAMENTO ODP/ODPV": {
        bg: "#edf2ff",
        color: "#364fc7",
        icon: <CheckCircle size={12} />,
      },
      EVASO: {
        bg: "#ebfbee",
        color: "#2b8a3e",
        icon: <CheckCircle size={12} />,
      },
    };
    return (
      map[stato] || {
        bg: "#f8f9fa",
        color: "#495057",
        icon: <Layers size={12} />,
      }
    );
  };
  const filteredOrders = urgentOrders.filter((order) => {
    const orderDate = order["Data Ordine cli"]
      ? order["Data Ordine cli"].split(" ")[0]
      : "";
    const s = searchTerm.toLowerCase();

    const matchesSearch =
      order["Num Ord Cli"]?.toString().toLowerCase().includes(s) ||
      order["Num Doc"]?.toString().toLowerCase().includes(s) ||
      order["Articolo cli"]?.toString().toLowerCase().includes(s) ||
      order["Rag Soc Cli"]?.toString().toLowerCase().includes(s) ||
      order["Ragione sociale"]?.toString().toLowerCase().includes(s) ||
      order["ID Cliente"]?.toString().toLowerCase().includes(s);

    const matchesStatus =
      statusFilter === "TUTTI" || order.calculatedStatus === statusFilter;

    const matchesStart = dateRange.start === "" || orderDate >= dateRange.start;
    const matchesEnd = dateRange.end === "" || orderDate <= dateRange.end;

    const hasNoClient =
      !order["ID Cliente"] ||
      order["ID Cliente"] === "(NULL)" ||
      !order["Rag Soc Cli"];
    const matchesNoClient = !onlyNoClient || hasNoClient;
    const matchesHideNoClient = !hideNoClient || !hasNoClient;
    const matchesCausale = !onlyWithCausale || hasCausale(order);

    const traffic = getTrafficLight(order);
    const matchesTraffic =
      trafficFilters.length === 0 || trafficFilters.includes(traffic.key);

    // Evasi parziali: di default nasconde le righe con
    // Qta DEV cli = 0 E Qta DEV fornitore = 0.
    const matchesPartialEvaded = matchesPartialEvadedFilter(order);

    return (
      matchesSearch &&
      matchesStatus &&
      matchesStart &&
      matchesEnd &&
      matchesNoClient &&
      matchesHideNoClient &&
      matchesCausale &&
      matchesTraffic &&
      matchesPartialEvaded
    );
  });

  const sortedAndFilteredOrders = [...filteredOrders].sort((a, b) => {
    if (sortBy === "ALPHA_ASC")
      return String(a["Articolo cli"] || "").localeCompare(
        String(b["Articolo cli"] || ""),
      );
    if (sortBy === "DATE_DESC") {
      return (
        new Date(
          b["Data Ordine cli"] ? b["Data Ordine cli"].split(" ")[0] : 0,
        ) -
        new Date(a["Data Ordine cli"] ? a["Data Ordine cli"].split(" ")[0] : 0)
      );
    }
    if (sortBy === "STATUS_ASC") {
      return (
        (STATUS_HIERARCHY[a.calculatedStatus] || 0) -
        (STATUS_HIERARCHY[b.calculatedStatus] || 0)
      );
    }
    return getGroupKey(a).localeCompare(getGroupKey(b));
  });

  const resetFilters = async () => {
    setStatusFilter("TUTTI");

    const defaultRange = getDefaultDateRange();

    setDateRange(defaultRange);

    setOnlyNoClient(false);
    setHideNoClient(false);
    setOnlyWithCausale(false);

    // Il filtro Evasi parziali e' attivo anche dopo il Reset.
    setPartialEvadedFilter(true);

    setTrafficFilters([]);
    setSearchTerm("");

    await loadOrders(defaultRange.start, defaultRange.end);
  };

  const renderTimelineStep = (
    label,
    dateString,
    infoAggiuntiva = "",
    isCurrentStatus = false,
  ) => {
    const isDone =
      dateString &&
      dateString !== "null" &&
      dateString !== null &&
      dateString !== "(NULL)";
    return (
      <Col className="text-center px-1">
        <div
          style={{
            fontSize: "0.6rem",
            fontWeight: isCurrentStatus ? "900" : "800",
            color: isCurrentStatus ? "#dc3545" : isDone ? "#0d6efd" : "#adb5bd",
            textTransform: "uppercase",
            marginBottom: "4px",
            minHeight: "24px",
          }}
        >
          {label}
        </div>
        {infoAggiuntiva && (
          <div
            style={{ fontSize: "0.55rem", color: "#6c757d", minHeight: "14px" }}
          >
            {isDone ? infoAggiuntiva : ""}
          </div>
        )}
        <div
          style={{
            height: "4px",
            backgroundColor: isCurrentStatus
              ? "#dc3545"
              : isDone
                ? "#0d6efd"
                : "#e9ecef",
            borderRadius: "2px",
            marginTop: "8px",
            marginBottom: "8px",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-5px",
              left: "50%",
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              backgroundColor: isCurrentStatus
                ? "#dc3545"
                : isDone
                  ? "#0d6efd"
                  : "#dee2e6",
              transform: "translateX(-50%)",
              border: isCurrentStatus ? "2px solid white" : "none",
              boxShadow: isCurrentStatus
                ? "0 0 8px rgba(220, 53, 69, 0.6)"
                : "none",
            }}
          ></div>
        </div>
        <div
          style={{
            fontSize: "0.65rem",
            color: isCurrentStatus ? "#dc3545" : isDone ? "#212529" : "#dee2e6",
            fontWeight: isDone ? "600" : "400",
          }}
        >
          {isDone
            ? new Date(dateString.replace(/-/g, "/")).toLocaleDateString(
                "it-IT",
              )
            : "--"}
        </div>
      </Col>
    );
  };

  const renderTrafficToggle = () => (
    <div className="d-flex align-items-center gap-1 bg-white rounded-3 shadow-sm px-2 traffic-toggle-group">
      {TRAFFIC_OPTIONS.map((opt) => {
        const active = trafficFilters.includes(opt.key);
        return (
          <OverlayTrigger
            key={opt.key}
            placement="top"
            overlay={<Tooltip>{opt.label}</Tooltip>}
          >
            <button
              type="button"
              onClick={() => toggleTrafficFilter(opt.key)}
              className="traffic-toggle-btn"
              style={{
                border: active
                  ? `1.5px solid ${opt.color}`
                  : "1.5px solid transparent",
                backgroundColor: active ? opt.bg : "transparent",
                color: opt.color,
              }}
            >
              <span
                style={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: opt.color,
                  display: "inline-block",
                  boxShadow: active ? `0 0 6px ${opt.color}` : "none",
                }}
              />
            </button>
          </OverlayTrigger>
        );
      })}
    </div>
  );

  const renderTopSearchBar = () => (
    <div className="d-flex gap-2 align-items-center">
      <div className="position-relative">
        <Search
          size={16}
          className="position-absolute"
          style={{
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#adb5bd",
          }}
        />

        <Form.Control
          size="sm"
          className="border-0 shadow-sm"
          placeholder="Cerca cliente, ordine, articolo, fornitore..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            borderRadius: "10px",
            width: "290px",
            paddingLeft: "38px",
            height: "38px",
            fontSize: "0.8rem",
          }}
        />
      </div>

      {renderTrafficToggle()}

      {/* PULSANTE FILTRI */}
      <Button
        variant="light"
        size="sm"
        className="d-flex align-items-center gap-2 fw-bold shadow-sm"
        style={{
          height: "38px",
          borderRadius: "10px",
          border: "1px solid #dee2e6",
          whiteSpace: "nowrap",
        }}
        onClick={() => setShowFiltersCanvas(true)}
      >
        <FiSliders size={15} />
        Filtri
      </Button>
    </div>
  );
  const renderTableContent = (isFullscreen = false) => {
    // Somma l'importo di tutte le righe che condividono lo stesso gruppo
    // (Num Ord Cli, o ordine fornitore quando manca l'ordine cliente)
    // a partire dall'indice indicato (i gruppi sono blocchi contigui nell'array ordinato)
    const calcGroupTotal = (startIndex) => {
      const key = getGroupKey(sortedAndFilteredOrders[startIndex]);
      let total = 0;
      for (let i = startIndex; i < sortedAndFilteredOrders.length; i++) {
        if (getGroupKey(sortedAndFilteredOrders[i]) !== key) break;
        const riga = sortedAndFilteredOrders[i];
        total += Number(riga["Netto cli"]) || 0;
      }
      return total;
    };

    return (
      <Table className="custom-table" responsive="true">
        <thead>
          <tr
            className="text-muted"
            style={{
              fontSize: "0.65rem",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            <th
              onClick={() =>
                setSortBy(sortBy === "ALPHA_ASC" ? "DOC_ASC" : "ALPHA_ASC")
              }
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Articolo
              <ArrowUpDown
                size={12}
                className="ms-1"
                style={{
                  color: sortBy === "ALPHA_ASC" ? "#0d6efd" : "#adb5bd",
                }}
              />
            </th>
            <th
              onClick={() =>
                setSortBy(sortBy === "DATE_DESC" ? "DOC_ASC" : "DATE_DESC")
              }
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Data Ord. Cliente{" "}
              <ArrowUpDown
                size={12}
                className="ms-1"
                style={{
                  color: sortBy === "DATE_DESC" ? "#0d6efd" : "#adb5bd",
                }}
              />
            </th>

            <th>Importo Cliente</th>
            {isFullscreen && <th className="text-center">Data Cliente</th>}

            <th>Data Ord. Fornitore</th>
            <th>Importo Fornitore</th>
            {isFullscreen && <th className="text-center">Data Fornitore</th>}

            <th
              onClick={() =>
                setSortBy(sortBy === "STATUS_ASC" ? "DOC_ASC" : "STATUS_ASC")
              }
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Stato Avanzamento{" "}
              <ArrowUpDown
                size={12}
                className="ms-1"
                style={{
                  color: sortBy === "STATUS_ASC" ? "#0d6efd" : "#adb5bd",
                }}
              />
            </th>
            <th
              className="text-center"
              style={{ width: isFullscreen ? "150px" : "30px" }}
            >
              {isFullscreen ? "Stato Tempi" : ""}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredOrders.length === 0 ? (
            <tr>
              <td
                colSpan={isFullscreen ? 9 : 7}
                className="text-center text-muted py-4 small"
              >
                Nessun ordine cliente trovato.
              </td>
            </tr>
          ) : (
            sortedAndFilteredOrders.map((ordine, index) => {
              const groupKey = getGroupKey(ordine);
              const showHeader =
                index === 0 ||
                groupKey !== getGroupKey(sortedAndFilteredOrders[index - 1]);
              const currentStyle = getStatusStyle(ordine.calculatedStatus);
              const traffic = getTrafficLight(ordine);

              const dataFornitore = getMostRecentDateStr(
                ordine["Data Conferma Fornitori"],
                ordine["Data Effettiva Fornitori"],
              );
              const dataCliente = getMostRecentDateStr(
                ordine["Data Prev Consegna Cli"],
                ordine["Data Effettiva Consegna Cli"],
              );

              return (
                <React.Fragment key={index}>
                  {showHeader && (
                    <tr className="doc-header-row">
                      <td colSpan={isFullscreen ? 9 : 7}>
                        <div
                          className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                          style={{ fontSize: "0.75rem", fontWeight: "600" }}
                        >
                          <div className="d-flex fw-bold align-items-center gap-2 flex-wrap">
                            {hasClientOrder(ordine) ? (
                              <>
                                {ordine["Tipo Ord Cli"] || ""}{" "}
                                {ordine["Num Ord Cli"] || "N/D"}
                                <span>|</span>
                              </>
                            ) : (
                              <>
                                <span className="badge bg-secondary-subtle text-secondary fw-bold">
                                  Rif. Ordine Fornitore
                                </span>
                                <span>|</span>
                              </>
                            )}
                            <strong className="text-dark fw-bold d-flex align-items-center gap-1">
                              {ordine["Rag Soc Cli"] &&
                              ordine["Rag Soc Cli"] !== "(NULL)" ? (
                                `${ordine["Rag Soc Cli"]} (${ordine["ID Cliente"]})`
                              ) : (
                                <span className="text-danger fw-bold">
                                  NON ASSEGNATO
                                </span>
                              )}
                            </strong>
                            <span>|</span>
                            Tot: € {calcGroupTotal(index).toFixed(2)}
                          </div>
                          <div className="text-dark d-flex align-items-center gap-2">
                            <span className="d-flex align-items-center gap-1">
                              {ordine["Tipo Doc"] || ""}{" "}
                              {ordine["Num Doc"] || "N/D"}
                            </span>
                            <span className="text-muted">|</span>
                            <span>{ordine["Ragione sociale"] || "N/D"}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr
                    className="item-row shadow-sm"
                    onClick={() => handleOpenModal(ordine)}
                  >
                    {/* Articolo */}
                    <td>
                      <div
                        className="fw-bold text-dark"
                        style={{ fontSize: "0.82rem" }}
                      >
                        {ordine["Articolo cli"] || ordine["Articolo"]}
                      </div>
                    </td>

                    {/* Data Ord. Cliente */}
                    <td style={{ fontSize: "0.78rem" }}>
                      {ordine["Data Ordine cli"]
                        ? new Date(
                            ordine["Data Ordine cli"].split(" ")[0],
                          ).toLocaleDateString("it-IT")
                        : "--"}
                    </td>

                    {/* Importo Cliente */}
                    <td style={{ fontSize: "0.78rem" }}>
                      € {Number(ordine["Netto cli"]).toFixed(2)}
                    </td>

                    {/* Data Cliente (solo fullscreen) */}
                    {isFullscreen && (
                      <td
                        className="text-center"
                        style={{ fontSize: "0.78rem" }}
                      >
                        {formatDateOrDash(dataCliente)}
                      </td>
                    )}

                    {/* Data Ord. Fornitore */}
                    <td style={{ fontSize: "0.78rem" }}>
                      {ordine["Data Ordine"]
                        ? new Date(
                            ordine["Data Ordine"].split(" ")[0],
                          ).toLocaleDateString("it-IT")
                        : "--"}
                    </td>

                    {/* Importo Fornitore */}
                    <td style={{ fontSize: "0.78rem" }}>
                      € {Number(ordine["Netto for"]).toFixed(2)}
                    </td>

                    {/* Data Fornitore (solo fullscreen) */}
                    {isFullscreen && (
                      <td
                        className="text-center"
                        style={{ fontSize: "0.78rem" }}
                      >
                        {formatDateOrDash(dataFornitore)}
                      </td>
                    )}

                    {/* Stato Avanzamento */}
                    <td>
                      <div
                        className="status-badge"
                        style={{
                          backgroundColor: currentStyle.bg,
                          color: currentStyle.color,
                          borderRadius: "6px",
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        {currentStyle.icon} {ordine.calculatedStatus}
                      </div>
                    </td>

                    {/* Stato Tempi / Semaforo */}
                    <td
                      className={`text-center${!isFullscreen ? " semaforo-cell-compact" : ""}`}
                    >
                      {isFullscreen ? (
                        <div
                          className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded-pill"
                          style={{
                            backgroundColor: traffic.bg,
                            fontSize: "0.7rem",
                            fontWeight: "700",
                            color: traffic.color,
                          }}
                        >
                          <span
                            style={{
                              width: "9px",
                              height: "9px",
                              borderRadius: "50%",
                              backgroundColor: traffic.color,
                              display: "inline-block",
                              boxShadow: `0 0 6px ${traffic.color}`,
                            }}
                          />
                          {traffic.label}
                        </div>
                      ) : (
                        <span
                          style={{
                            width: "9px",
                            height: "9px",
                            borderRadius: "50%",
                            backgroundColor: traffic.color,
                            display: "inline-block",
                            boxShadow: `0 0 6px ${traffic.color}`,
                          }}
                        />
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })
          )}
        </tbody>{" "}
      </Table>
    );
  };

  const qtaOrdinataCli = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.cliOrdinata] || 0)
    : 0;

  const qtaEvasoCli = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.cliEvasa] || 0)
    : 0;

  const qtaDevCli = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.cliDev] || 0)
    : 0;

  const qtaDaEvadereCli = Math.max(0, qtaOrdinataCli - qtaEvasoCli - qtaDevCli);

  // Quantità rimessa
  const qtaRimessaCli = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forRimessa] || 0)
    : 0;

  const qtaOrdinataFor = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forOrdinata] || 0)
    : 0;

  const qtaRimessaFor = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forRimessa] || 0)
    : 0;

  const qtaEvasoFor = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forEvasa] || 0)
    : 0;

  const qtaDevFor = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forDev] || 0)
    : 0;

  const qtaDaEvadereFor = Math.max(0, qtaOrdinataFor - qtaEvasoFor - qtaDevFor);

  return (
    <div
      className="bg-white"
      style={{
        maxHeight: "420px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: "1px solid #eee",
        borderRadius: "10px",
      }}
    >
      <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <span
            onClick={() => setShowFullscreenModal(true)}
            style={{
              fontWeight: "800",
              fontSize: "0.78rem",
              color: "#1a1a1a",
              letterSpacing: "0.5px",
              cursor: "pointer",
            }}
          >
            MONITORAGGIO ORDINI CLIENTE
          </span>

          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>Come funziona questo widget</Tooltip>}
          >
            <button
              type="button"
              className="widget-info-btn"
              aria-label="Informazioni sul widget Monitoraggio Ordini Cliente"
              onClick={() => setShowInfoModal(true)}
            >
              <FiInfo size={15} />
            </button>
          </OverlayTrigger>
        </div>
        {renderTopSearchBar()}
      </div>

      <style>{`
        .scroll-area { overflow-y: auto; flex-grow: 1; padding: 15px; background-color: #f8f9fa; }
        .custom-table { border-collapse: separate; border-spacing: 0 6px; }
        .doc-header-row td { background: #e9ecef !important; border: none !important; border-radius: 6px; padding: 8px 15px !important; }
        .item-row { cursor: pointer; transition: all 0.15s; }
        .item-row td { background: white !important; border: none !important; padding: 10px 15px !important; vertical-align: middle; }
        .item-row:hover td { background: #f1f7fe !important; }
        .item-row td:first-child { border-top-left-radius: 10px; border-bottom-left-radius: 10px; }
        .item-row td:last-child { border-top-right-radius: 10px; border-bottom-right-radius: 10px; }
        .status-badge { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; font-weight: 700; font-size: 0.65rem; text-transform: uppercase; white-space: nowrap; }
        .semaforo-cell-compact { width: 34px !important; padding: 10px 6px !important; }
        .stato-avanzamento-th { width: auto; }
        .row-cols-7 > * { flex: 0 0 auto; width: 14.2857%; }
        .canvas-section-title { font-size: 0.75rem; font-weight: 700; color: #495057; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
        .traffic-toggle-group { height: 38px; }
        .traffic-toggle-btn { border-radius: 8px; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; background: transparent; transition: all 0.15s; }
        .traffic-toggle-btn:hover { background-color: #f1f3f5; }
        .widget-info-btn { width: 27px; height: 27px; border: 1px solid #dfe3e8; border-radius: 50%; background: #fff; color: #0d6efd; display: inline-flex; align-items: center; justify-content: center; padding: 0; transition: all 0.15s ease; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .widget-info-btn:hover { background: #e7f1ff; border-color: #b6d4fe; transform: translateY(-1px); }
        .widget-info-backdrop { z-index: 20990 !important; }
        .widget-info-card { border: 1px solid #e9ecef; border-radius: 14px; background: #fff; padding: 16px; height: 100%; }
        .widget-info-step { width: 28px; height: 28px; min-width: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.72rem; font-weight: 800; background: #e7f1ff; color: #0d6efd; }
        .widget-info-label { font-size: 0.68rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px; color: #6c757d; }
        .widget-info-list { margin: 0; padding-left: 18px; color: #495057; font-size: 0.82rem; }
        .widget-info-list li + li { margin-top: 6px; }
      `}</style>

      <div className="scroll-area">{renderTableContent(false)}</div>

      {/* MODALE INFORMAZIONI WIDGET */}
 <Modal
        show={showInfoModal}
        className="p-5"
        onHide={() => setShowInfoModal(false)}
        centered
        size="lg"
        scrollable
        style={{ zIndex: 21000 }}
        backdropClassName="widget-info-backdrop"
      >
        <Modal.Header closeButton className="border-bottom bg-white px-4 py-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
              style={{ width: "40px", height: "40px" }}
            >
              <FiInfo size={20} />
            </div>
            <div>
              <Modal.Title className="fw-bold" style={{ fontSize: "1.05rem" }}>
                Come usare Monitoraggio Ordini Cliente
              </Modal.Title>
              <div className="text-muted mt-1" style={{ fontSize: "0.78rem" }}>
                Guida rapida per capire cosa mostra il widget, come navigarlo e
                come usare ricerca, filtri e dettaglio ordine.
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          <div className="bg-primary-subtle border border-primary-subtle rounded-4 p-3 mb-3">
            <div className="fw-bold text-primary mb-1">A cosa serve</div>
            <div
              className="text-dark"
              style={{ fontSize: "0.84rem", lineHeight: 1.55 }}
            >
              Questo widget raccoglie in un'unica vista gli ordini cliente
              collegati ai relativi ordini fornitore. Ti permette di controllare
              rapidamente articolo, date, importi, stato di avanzamento e
              rispetto dei tempi, per poi aprire il dettaglio completo della
              singola riga quando serve approfondire.
            </div>
          </div>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="widget-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="widget-info-step">1</span>
                  <div>
                    <div className="fw-bold text-dark">
                      Aprire e navigare il widget
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Vista compatta e vista estesa
                    </div>
                  </div>
                </div>
                <ul className="widget-info-list">
                  <li>
                    Il riquadro mostra subito una versione compatta dell'elenco
                    ordini.
                  </li>
                  <li>
                    Clicca sul titolo{" "}
                    <strong>MONITORAGGIO ORDINI CLIENTE</strong> per aprire la
                    vista a schermo intero.
                  </li>
                  <li>
                    Nella vista estesa trovi più spazio e due colonne
                    aggiuntive: <strong>Data Cliente</strong> e{" "}
                    <strong>Data Fornitore</strong>.
                  </li>
                  <li>
                    Clicca su una riga dell'ordine per aprire la scheda di
                    dettaglio.
                  </li>
                </ul>
              </div>
            </Col>

            <Col md={6}>
              <div className="widget-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="widget-info-step">2</span>
                  <div>
                    <div className="fw-bold text-dark">Ricerca veloce</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Trova subito la riga che ti interessa
                    </div>
                  </div>
                </div>
                <ul className="widget-info-list">
                  <li>Usa la barra di ricerca in alto a destra.</li>
                  <li>
                    Puoi cercare per <strong>numero ordine cliente</strong>,{" "}
                    <strong>numero documento fornitore</strong>,{" "}
                    <strong>articolo</strong>, <strong>cliente</strong>,{" "}
                    <strong>fornitore</strong> o <strong>ID cliente</strong>.
                  </li>
                  <li>
                    La ricerca si combina con tutti gli altri filtri attivi.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="widget-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="widget-info-step">3</span>
                  <div>
                    <div className="fw-bold text-dark">Semaforo dei tempi</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Filtro rapido a tre colori
                    </div>
                  </div>
                </div>
                <div
                  className="d-flex flex-column gap-2"
                  style={{ fontSize: "0.82rem" }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <span
                      className="mt-1 rounded-circle flex-shrink-0"
                      style={{
                        width: "10px",
                        height: "10px",
                        background: "#dc3545",
                      }}
                    />
                    <div>
                      <strong>Rosso — Nessuna Data:</strong> segnala situazioni
                      senza date fornitore utili al monitoraggio.
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2">
                    <span
                      className="mt-1 rounded-circle flex-shrink-0"
                      style={{
                        width: "10px",
                        height: "10px",
                        background: "#f59f00",
                      }}
                    />
                    <div>
                      <strong>Giallo — In Ritardo:</strong> indica che la data
                      odierna ha superato le date di riferimento disponibili.
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2">
                    <span
                      className="mt-1 rounded-circle flex-shrink-0"
                      style={{
                        width: "10px",
                        height: "10px",
                        background: "#2b8a3e",
                      }}
                    />
                    <div>
                      <strong>Verde — Nei Tempi / Evaso:</strong> indica un
                      ordine nei tempi oppure già completato/evaso.
                    </div>
                  </div>
                  <div
                    className="text-muted pt-1"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Puoi selezionare anche più colori insieme. Se non selezioni
                    nessun colore, vengono mostrati tutti.
                  </div>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="widget-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="widget-info-step">4</span>
                  <div>
                    <div className="fw-bold text-dark">Pannello Filtri</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Aprilo dal pulsante “Filtri”
                    </div>
                  </div>
                </div>
                <ul className="widget-info-list">
                  <li>
                    <strong>Data Ordine Cliente:</strong> imposta un intervallo
                    data da/a.
                  </li>
                  <li>
                    <strong>Stato Avanzamento:</strong> filtra per Ordinato,
                    Rimesso, Entrato in magazzino, BEM, ODP/ODPV o Evaso.
                  </li>
                  <li>
                    <strong>Cliente Assegnato:</strong> mostra solo ordini senza
                    cliente oppure nascondili.
                  </li>
                  <li>
                    <strong>Evasi parziali:</strong> attivo di default; nasconde
                    le righe in cui cliente e fornitore hanno entrambi Da Evadere
                    uguale a 0.
                  </li>
                  <li>
                    <strong>Causale:</strong> mostra solo gli ordini che hanno
                    una causale valorizzata.
                  </li>
                  <li>
                    <strong>Reset</strong> ripristina i filtri;{" "}
                    <strong>Applica</strong> chiude il pannello mantenendo le
                    scelte effettuate.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          <div className="widget-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="widget-info-step">5</span>
              <div>
                <div className="fw-bold text-dark">Come leggere la tabella</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Dati principali e raggruppamento degli ordini
                </div>
              </div>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <div className="widget-info-label mb-2">
                  Intestazione del gruppo
                </div>
                <ul className="widget-info-list">
                  <li>
                    Ordine cliente, quando presente; altrimenti viene indicato
                    il riferimento all'ordine fornitore.
                  </li>
                  <li>Cliente e relativo ID.</li>
                  <li>Totale economico del gruppo.</li>
                  <li>Documento e ragione sociale del fornitore.</li>
                </ul>
              </Col>
              <Col md={6}>
                <div className="widget-info-label mb-2">Riga articolo</div>
                <ul className="widget-info-list">
                  <li>Articolo.</li>
                  <li>Data e importo dell'ordine cliente.</li>
                  <li>Data e importo dell'ordine fornitore.</li>
                  <li>Stato di avanzamento calcolato.</li>
                  <li>Stato tempi, rappresentato dal semaforo.</li>
                </ul>
              </Col>
            </Row>
            <div
              className="mt-3 p-2 px-3 rounded-3 bg-light border"
              style={{ fontSize: "0.78rem" }}
            >
              <strong>Ordinamento:</strong> clicca sulle intestazioni{" "}
              <strong>Articolo</strong>, <strong>Data Ord. Cliente</strong> o{" "}
              <strong>Stato Avanzamento</strong> per cambiare l'ordinamento.
            </div>
          </div>

          <div className="widget-info-card">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="widget-info-step">6</span>
              <div>
                <div className="fw-bold text-dark">
                  Cosa trovi nel dettaglio ordine
                </div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Apri il dettaglio cliccando una riga
                </div>
              </div>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <ul className="widget-info-list">
                  <li>
                    <strong>Timeline logistica:</strong> Ordine, Rimesso,
                    Entrata magazzino, Invio/Completamento BEM,
                    Invio/Completamento ODPV.
                  </li>
                  <li>
                    <strong>Dati Cliente:</strong> ordine, cliente, data ordine
                    e date di consegna concordata, prevista ed effettiva.
                  </li>
                  <li>
                    <strong>Dati Fornitore:</strong> ordine, fornitore, data
                    ordine, data conferma e data effettiva.
                  </li>
                </ul>
              </Col>
              <Col md={6}>
                <ul className="widget-info-list">
                  <li>
                    <strong>Quantità Cliente:</strong> ordinata, da evadere,
                    evasa e rimessa.
                  </li>
                  <li>
                    <strong>Quantità Fornitore:</strong> ordinata, da evadere,
                    evasa e rimessa.
                  </li>
                  <li>
                    Le date che mostrano l'icona a forma di matita possono
                    essere aperte in modifica.
                  </li>
                </ul>
              </Col>
            </Row>
          </div>

          {/* SEZIONE 7 - SIGNIFICATO DELLE DATE */}
          <div className="widget-info-card mt-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="widget-info-step">7</span>
              <div>
                <div className="fw-bold text-dark">Significato delle date</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Come vengono lette e calcolate le principali date di fornitura e consegna
                </div>
              </div>
            </div>

            <div
              className="p-3 rounded-3 bg-light border mb-3"
              style={{ fontSize: "0.8rem", lineHeight: 1.55 }}
            >
              <strong>Regola generale:</strong> per le date fornitore il sistema
              utilizza prima la data specifica della <strong>riga ordine</strong>.
              Se non disponibile, utilizza la data presente nella
              <strong> testata dell’ordine fornitore</strong>. Le date cliente
              calcolate tengono inoltre conto dei <strong>giorni di spedizione</strong>
              configurati per il fornitore.
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="widget-info-label mb-2">Date Fornitore</div>
                <ul className="widget-info-list">
                  <li>
                    <strong>Data Conferma Fornitori:</strong> prima data di
                    consegna confermata dal fornitore. Il sistema usa la data
                    della riga ordine e, se assente, quella della testata ordine.
                  </li>
                  <li>
                    <strong>Data Effettiva Fornitori:</strong> data aggiornata
                    associata alla consegna del fornitore. Anche in questo caso
                    viene privilegiato il valore della riga e, se non presente,
                    quello della testata.
                  </li>
                  <li>
                    <strong>Data proposta:</strong> data proposta sull’ordine
                    fornitore utilizzata come riferimento per il controllo dei
                    tempi. Se la data è superata, il sistema può segnalare
                    l’ordine in ritardo.
                  </li>
                </ul>
              </Col>

              <Col md={6}>
                <div className="widget-info-label mb-2">Date Cliente</div>
                <ul className="widget-info-list">
                  <li>
                    <strong>Data Prev Consegna Cli:</strong> data prevista di
                    spedizione/consegna registrata sull’ordine cliente.
                  </li>
                  <li>
                    <strong>Data Promessa Cli:</strong> data promessa al cliente,
                    calcolata partendo dalla <strong>Data Conferma Fornitori</strong>
                    e aggiungendo i giorni di spedizione configurati per il
                    fornitore.
                  </li>
                  <li>
                    <strong>Data Concordata Consegna:</strong> data di consegna
                    aggiornata lato cliente, calcolata partendo dalla
                    <strong> Data Effettiva Fornitori</strong> e aggiungendo i
                    giorni di spedizione del fornitore.
                  </li>
                </ul>
              </Col>
            </Row>

            <div
              className="mt-3 p-2 px-3 rounded-3 bg-primary-subtle border border-primary-subtle"
              style={{ fontSize: "0.78rem", lineHeight: 1.5 }}
            >
              <strong>Esempio:</strong> se il fornitore conferma la disponibilità
              per il 10/09 e sono previsti 2 giorni di spedizione, la
              <strong> Data Promessa Cli</strong> sarà il 12/09. Se successivamente
              il fornitore aggiorna la propria data effettiva al 13/09, la
              <strong> Data Concordata Consegna</strong> diventerà il 15/09.
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-between">
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
            Suggerimento: usa prima ricerca e semaforo, poi affina il risultato
            con il pannello Filtri.
          </div>
          <div className="d-flex gap-2">
            <Button variant="light" onClick={() => setShowInfoModal(false)}>
              Chiudi
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowInfoModal(false);
                setShowFullscreenModal(true);
              }}
            >
              Apri vista estesa
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {/* OFFCANVAS FILTRI */}
      <Offcanvas
        show={showFiltersCanvas}
        onHide={() => setShowFiltersCanvas(false)}
        placement="end"
        className="orders-filter-canvas"
        backdropClassName="orders-filter-backdrop"
        style={{
          borderRadius: "16px 0 0 16px",
          zIndex: 20000,
        }}
      >
        <Offcanvas.Header closeButton className="border-bottom bg-light">
          <Offcanvas.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "1.05rem" }}
          >
            <FiSliders className="text-primary" /> Filtri Ordini Cliente
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column justify-content-between p-4">
          <div className="d-flex flex-column gap-4">
            {/* Range Date Ordine Cliente */}
            <div>
              <div className="canvas-section-title d-flex align-items-center gap-1">
                <Calendar size={14} /> Data Ordine Cliente
              </div>
              <Row className="g-2">
                <Col>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, start: e.target.value })
                    }
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, end: e.target.value })
                    }
                  />
                </Col>
              </Row>
            </div>

            {/* Stato Calcolato */}
            <div>
              {/* SEZIONE QUANTITÀ CLIENTE */}

              <div className="canvas-section-title">Stato Avanzamento</div>
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ fontWeight: "600" }}
              >
                <option value="TUTTI">TUTTI GLI STATI</option>
                <option value="ORDINATO">ORDINATO</option>
                <option value="RIMESSO">RIMESSO</option>
                <option value="ENTRATO IN MAGAZZINO">
                  ENTRATO IN MAGAZZINO
                </option>
                <option value="BEM INVIATA">BEM INVIATA</option>
                <option value="BEM COMPLETATA">BEM COMPLETATA</option>
                <option value="INVIO ODP/ODPV">INVIO ODP/ODPV</option>
                <option value="COMPLETAMENTO ODP/ODPV">COMPL. ODP/ODPV</option>
                <option value="EVASO">EVASO</option>
              </Form.Select>
            </div>

            {/* Cliente assegnato */}
            <div>
              <div className="canvas-section-title d-flex align-items-center gap-1">
                <User size={14} /> Cliente Assegnato
              </div>
              <Form.Check
                type="checkbox"
                id="filter-only-no-client"
                label="Mostra solo ordini senza cliente"
                checked={onlyNoClient}
                onChange={(e) => {
                  setOnlyNoClient(e.target.checked);
                  if (e.target.checked) setHideNoClient(false);
                }}
                className="mb-2 fw-semibold"
              />
              <Form.Check
                type="checkbox"
                id="filter-hide-no-client"
                label="Nascondi ordini senza cliente"
                checked={hideNoClient}
                onChange={(e) => {
                  setHideNoClient(e.target.checked);
                  if (e.target.checked) setOnlyNoClient(false);
                }}
                className="fw-semibold"
              />
            </div>

            {/* Evasi parziali */}
            <div>
              <div className="canvas-section-title d-flex align-items-center gap-1">
                <CheckCircle size={14} /> Evasi parziali
              </div>
              <Form.Check
                type="checkbox"
                id="filter-partial-evaded"
                label="Evasi parziali"
                checked={partialEvadedFilter}
                onChange={(e) => setPartialEvadedFilter(e.target.checked)}
                className="fw-semibold"
              />
              <div
                className="text-muted mt-1"
                style={{ fontSize: "0.72rem", lineHeight: 1.35 }}
              >
                Se attivo, nasconde gli ordini con Da Evadere cliente e
                fornitore entrambi a 0.
              </div>
            </div>

            {/* Causale */}
            <div>
              <div className="canvas-section-title d-flex align-items-center gap-1">
                <FileText size={14} /> Causale
              </div>
              <Form.Check
                type="checkbox"
                id="filter-only-causale"
                label="Mostra solo ordini con causale"
                checked={onlyWithCausale}
                onChange={(e) => setOnlyWithCausale(e.target.checked)}
                className="fw-semibold"
              />
            </div>
          </div>

          <div className="d-flex gap-2 border-top pt-3 bg-white">
            <Button
              variant="outline-secondary"
              size="sm"
              className="w-50"
              onClick={resetFilters}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="w-50 fw-bold"
              onClick={async () => {
                // Ricarica realmente i dati dal PHP usando l'intervallo scelto.
                // Non applichiamo nessuna esclusione aggiuntiva lato React:
                // le esclusioni restano quelle definite nella query SQL.
                await loadOrders(dateRange.start, dateRange.end);
                setShowFiltersCanvas(false);
              }}
            >
              Applica
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* MODALE FULLSCREEN */}
      <Modal
        show={showFullscreenModal}
        onHide={() => setShowFullscreenModal(false)}
        fullscreen
        style={{ zIndex: 1050 }}
        backdropClassName="fullscreen-monitor-backdrop"
      >
        <Modal.Body className="p-0 d-flex flex-column bg-light">
          <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center shadow-sm">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontWeight: "800", fontSize: "0.9rem" }}>
                MONITORAGGIO ORDINI CLIENTE — VISTA ESTESA
              </span>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Come funziona questo widget</Tooltip>}
              >
                <button
                  type="button"
                  className="widget-info-btn"
                  aria-label="Informazioni sul widget Monitoraggio Ordini Cliente"
                  onClick={() => setShowInfoModal(true)}
                >
                  <FiInfo size={15} />
                </button>
              </OverlayTrigger>
            </div>
            <div className="d-flex gap-3 align-items-center">
              {renderTopSearchBar()}
              <Button
                variant="outline-dark"
                className="d-flex align-items-center gap-1 border-0"
                onClick={() => setShowFullscreenModal(false)}
              >
                <X size={16} /> Chiudi
              </Button>
            </div>
          </div>
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
            <div className="bg-white p-3 shadow-sm rounded-4 border">
              {renderTableContent(true)}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODALE DETTAGLIO */}
<Modal
  show={showModal}
  onHide={() => setShowModal(false)}
  centered
  style={{ zIndex: 1070 }}
  dialogClassName="modal-modern"
  className="p-5"
  contentClassName="inline-edit-content2"
>
  {/* HEADER MODALE */}
  <Modal.Header className="bg-white border-bottom px-4 py-3 align-items-center">
    <div className="d-flex align-items-center justify-content-between w-100 me-3">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="badge bg-danger text-white px-2.5 py-1.5 rounded-pill fs-7 fw-bold">
          {selectedOrder?.Articolo}
        </span>
        <h6
          className="mb-0 fw-bold text-dark text-truncate"
          style={{ maxWidth: "320px" }}
        >
          {selectedOrder?.Descrizione}
        </h6>
      </div>

      <div className="d-flex flex-column align-items-end gap-1">
        
        <div className="d-flex align-items-center gap-2">
          <span className="text-uppercase text-muted fw-semibold fs-8 tracking-wider">
            Consegna:
          </span>
          {renderDeliveryBadge(selectedOrder)}
        </div>
      </div>
    </div>

    <Button
      variant="light"
      className="rounded-circle p-0 d-flex align-items-center justify-content-center text-muted flex-shrink-0 border-0"
      onClick={() => setShowModal(false)}
      style={{
        width: "28px",
        height: "28px",
        backgroundColor: "#f1f3f5",
      }}
    >
      <X size={16} />
    </Button>
  </Modal.Header>

  <Modal.Body className="p-3 bg-light fs-7">
    {/* TIMELINE LOGISTICA BILANCIATA */}
    <div className="p-3 bg-white rounded-3 shadow-sm border mb-3">
      <div className="timeline-container position-relative d-flex justify-content-between align-items-start py-2">
        <div className="timeline-line"></div>

        {selectedOrder && (
          <>
            {renderTimelineStep(
              "1. Ordine",
              selectedOrder["Data Ordine"],
              `Doc: ${selectedOrder["Num Doc"] || ""}`,
              currentStatus === "ORDINATO",
            )}
            {renderTimelineStep(
              "2. Rimesso",
              selectedOrder["Data Rimesso"],
              selectedOrder["numero_rimesso"]
                ? `N° ${selectedOrder["numero_rimesso"]}`
                : "Elaborato",
              currentStatus === "RIMESSO",
            )}
            {renderTimelineStep(
              "3. Entrata Mag.",
              selectedOrder["Data Entrata Magazzino"] ||
                selectedOrder["data_entrata"],
              selectedOrder["Num Entrata Magazzino"] ||
                selectedOrder["numero_entrata"]
                ? `N° ${selectedOrder["Num Entrata Magazzino"] || selectedOrder["numero_entrata"]}`
                : "Registrato",
              currentStatus === "ENTRATO IN MAGAZZINO",
            )}
            {renderTimelineStep(
              "4. Invio BEM",
              selectedOrder["bem_data_invio"],
              `BEM: ${selectedOrder["id_bem"] || ""}`,
              currentStatus === "BEM INVIATA",
            )}
            {renderTimelineStep(
              "5. BEM Compl.",
              selectedOrder["bem_data_copletamento"],
              `BEM: ${selectedOrder["id_bem"] || ""}`,
              currentStatus === "BEM COMPLETATA",
            )}
            {renderTimelineStep(
              "6. Invio ODPV",
              selectedOrder["Data Invio ODPV"],
              selectedOrder["ODPV"] ? `${selectedOrder["ODPV"]}` : "",
              currentStatus === "INVIO ODP/ODPV",
            )}
            {renderTimelineStep(
              "7. Compl. ODPV",
              selectedOrder["Data Completamento ODPV"],
              selectedOrder["ODPV"] ? `${selectedOrder["ODPV"]}` : "",
              currentStatus === "COMPLETAMENTO ODP/ODPV",
            )}
          </>
        )}
      </div>
    </div>

    {/* DATI FORNITORE E CLIENTE */}
    <Row className="g-2 mb-3">
      <Col md={6}>
        <div className="p-3 bg-white rounded-3 shadow-sm border h-100">
          <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
            <FileText size={14} className="text-primary" />
            <span className="mb-0 fw-bold text-dark fs-8 text-uppercase tracking-wider">
              Dati Cliente
            </span>
          </div>

          <div className="d-flex flex-column gap-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Ordine Cliente</span>
              <span className="fw-semibold text-dark">
                {selectedOrder?.["Tipo Ord Cli"]}{" "}
                <span className="text-danger">
                  {selectedOrder?.["Num Ord Cli"]}
                </span>
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Cliente</span>
              <span className="fw-semibold text-dark text-end">
                {selectedOrder?.["Rag Soc Cli"]}{" "}
                <span className="text-danger">
                  ({selectedOrder?.["ID Cliente"]})
                </span>
              </span>
            </div>

            {/* CARD DATI CLIENTE */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-dark fw-bold">
                Data Ordine Cliente
              </span>
              <span className="fw-bold text-dark">
                {selectedOrder?.["Data Ordine cli"]
                  ? new Date(
                      selectedOrder["Data Ordine cli"].split(" ")[0],
                    ).toLocaleDateString("it-IT")
                  : "—"}
              </span>
            </div>

            <hr />

            {/* 1. Data Promessa Cli */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Data Promessa</span>
              <div className="d-flex align-items-center gap-1.5">
                <span className="fw-bold text-secondary">
                  {selectedOrder?.["Data Promessa Cli"]
                    ? new Date(
                        selectedOrder["Data Promessa Cli"],
                      ).toLocaleDateString("it-IT")
                    : "—"}
                </span>
                <Button
                  variant="light"
                  size="sm"
                  className="p-1 rounded-circle border-0 bg-hover-gray"
                  onClick={() =>
                    setInlineEdit({
                      show: true,
                      field: "data_promessa_cli",
                      label: "Data Promessa",
                      value: selectedOrder?.["Data Promessa Cli"] || "",
                    })
                  }
                >
                  <Pencil size={10} className="text-muted" />
                </Button>
              </div>
            </div>

            {/* 2. Data Concordata Consegna */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Data Effettiva</span>
              <div className="d-flex align-items-center gap-1.5">
                <span className="fw-bold text-secondary">
                  {selectedOrder?.["Data Concordata Consegna"]
                    ? new Date(
                        selectedOrder["Data Concordata Consegna"],
                      ).toLocaleDateString("it-IT")
                    : "—"}
                </span>
                <Button
                  variant="light"
                  size="sm"
                  className="p-1 rounded-circle border-0 bg-hover-gray"
                  onClick={() =>
                    setInlineEdit({
                      show: true,
                      field: "data_concordata_consegna",
                      label: "Data Concordata",
                      value:
                        selectedOrder?.["Data Concordata Consegna"] || "",
                    })
                  }
                >
                  <Pencil size={10} className="text-muted" />
                </Button>
              </div>
            </div>

            {/* 3. Data Proposta */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Data Proposta</span>
              <div className="d-flex align-items-center gap-1.5">
                <span className="fw-bold text-secondary">
                  {selectedOrder?.["Data proposta"]
                    ? new Date(
                        selectedOrder["Data proposta"],
                      ).toLocaleDateString("it-IT")
                    : "—"}
                </span>
                <Button
                  variant="light"
                  size="sm"
                  className="p-1 rounded-circle border-0 bg-hover-gray"
                  onClick={() =>
                    setInlineEdit({
                      show: true,
                      field: "data_proposta",
                      label: "Data proposta",
                      value: selectedOrder?.["Data proposta"] || "",
                    })
                  }
                >
                  <Pencil size={10} className="text-muted" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Col>

      <Col md={6}>
        <div className="p-3 bg-white rounded-3 shadow-sm border h-100">
          <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
            <FileText size={14} className="text-primary" />
            <span className="mb-0 fw-bold text-dark fs-8 text-uppercase tracking-wider">
              Dati Fornitore
            </span>
          </div>

          <div className="d-flex flex-column gap-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Ordine Fornitore</span>
              <span className="fw-semibold text-dark">
                {selectedOrder?.["Tipo Doc"]}{" "}
                <span className="text-danger">
                  {selectedOrder?.["Num Doc"]}
                </span>
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Fornitore</span>
              <span className="fw-semibold text-dark text-end">
                {selectedOrder?.["Ragione sociale"]}{" "}
                <span className="text-danger">
                  ({selectedOrder?.["ID Fornitore"]})
                </span>
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <span className="text-dark fw-bold">
                Data Ordine Fornitore
              </span>
              <span className="fw-bold text-dark">
                {selectedOrder?.["Data Ordine"]
                  ? new Date(
                      selectedOrder["Data Ordine"].split(" ")[0],
                    ).toLocaleDateString("it-IT")
                  : "—"}
              </span>
            </div>

            <hr />

            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Data Conf. Fornitore</span>
              <div className="d-flex align-items-center gap-1.5">
                <span className="fw-bold text-secondary">
                  {selectedOrder?.["Data Conferma Fornitori"]
                    ? new Date(
                        selectedOrder["Data Conferma Fornitori"],
                      ).toLocaleDateString("it-IT")
                    : "—"}
                </span>
                <Button
                  variant="light"
                  size="sm"
                  className="p-1 rounded-circle border-0 bg-hover-gray"
                  onClick={() =>
                    setInlineEdit({
                      show: true,
                      field: "data_conferma_fornitori",
                      label: "Data Conferma Fornitore",
                      value:
                        selectedOrder?.["Data Conferma Fornitori"] || "",
                    })
                  }
                >
                  <Pencil size={10} className="text-muted" />
                </Button>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Data Effett. Fornitore</span>
              <div className="d-flex align-items-center gap-1.5">
                <span className="fw-bold text-secondary">
                  {selectedOrder?.["Data Effettiva Fornitori"]
                    ? new Date(
                        selectedOrder["Data Effettiva Fornitori"],
                      ).toLocaleDateString("it-IT")
                    : "—"}
                </span>
                <Button
                  variant="light"
                  size="sm"
                  className="p-1 rounded-circle border-0 bg-hover-gray"
                  onClick={() =>
                    setInlineEdit({
                      show: true,
                      field: "data_effettiva_fornitori",
                      label: "Data Effettiva Fornitore",
                      value:
                        selectedOrder?.["Data Effettiva Fornitori"] || "",
                    })
                  }
                >
                  <Pencil size={10} className="text-muted" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>

    {/* FEEDBACK SALVATAGGIO */}
    {(saveMsg || savingDates) && (
      <div className="mb-3 text-center">
        {savingDates ? (
          <span className="badge bg-primary-subtle text-primary py-1 px-3 fw-semibold fs-8 rounded-pill">
            <span
              className="spinner-border spinner-border-sm me-1.5"
              role="status"
              aria-hidden="true"
            ></span>
            Salvataggio in corso...
          </span>
        ) : (
          <span
            className={`badge ${saveMsg.includes("✅") ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"} py-1 px-3 fw-semibold fs-8 rounded-pill`}
          >
            {saveMsg}
          </span>
        )}
      </div>
    )}

    {/* SEZIONE QUANTITÀ AFFIANCATE (CLIENTE vs FORNITORE) */}
    <Row className="g-2">
      {/* COLONNA CLIENTE */}
      <Col md={6}>
        <div className="p-3 bg-white rounded-3 shadow-sm border h-100">
          <div className="canvas-section-title d-flex align-items-center gap-1 mb-2">
            <User size={14} /> Quantità Cliente
          </div>
          <Row className="g-2 row-cols-2">
            <Col>
              <div className="p-2 bg-light rounded-3 border position-relative overflow-hidden kpi-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-muted fw-bold fs-8 text-uppercase tracking-wider">
                    Ordinata
                  </span>
                  <div className="kpi-icon-wrapper bg-secondary-subtle text-secondary">
                    <Layers size={13} />
                  </div>
                </div>
                <div className="d-flex align-items-baseline gap-1">
                  <h5 className="fw-bold mb-0 text-dark">
                    {qtaOrdinataCli ?? 0}
                  </h5>
                  <span className="fs-8 text-muted">pz</span>
                </div>
              </div>
            </Col>

            <Col>
              <div className="p-2 bg-light rounded-3 border position-relative overflow-hidden kpi-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-warning-emphasis fw-bold fs-8 text-uppercase tracking-wider">
                    Da Evadere
                  </span>
                  <div className="kpi-icon-wrapper bg-warning-subtle text-warning">
                    <Clock size={13} />
                  </div>
                </div>
                <div className="d-flex align-items-baseline gap-1">
                  <h5 className="fw-bold mb-0 text-warning">
                    {qtaDaEvadereCli ?? 0}
                  </h5>
                  <span className="fs-8 text-warning-emphasis">pz</span>
                </div>
              </div>
            </Col>

            <Col>
              <div className="p-2 bg-light rounded-3 border position-relative overflow-hidden kpi-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-success-emphasis fw-bold fs-8 text-uppercase tracking-wider">
                    Evaso
                  </span>
                  <div className="kpi-icon-wrapper bg-success-subtle text-success">
                    <CheckCircle size={13} />
                  </div>
                </div>
                <div className="d-flex align-items-baseline gap-1">
                  <h5 className="fw-bold mb-0 text-success">
                    {qtaEvasoCli ?? 0}
                  </h5>
                  <span className="fs-8 text-success-emphasis">pz</span>
                </div>
              </div>
            </Col>

           
          </Row>
        </div>
      </Col>

      {/* COLONNA FORNITORE */}
      <Col md={6}>
        <div className="p-3 bg-white rounded-3 shadow-sm border h-100">
          <div className="canvas-section-title d-flex align-items-center gap-1 mb-2">
            <Building size={14} /> Quantità Fornitore
          </div>
          <Row className="g-2 row-cols-2">
            <Col>
              <div className="p-2 bg-light rounded-3 border position-relative overflow-hidden kpi-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-muted fw-bold fs-8 text-uppercase tracking-wider">
                    Ordinata
                  </span>
                  <div className="kpi-icon-wrapper bg-secondary-subtle text-secondary">
                    <Layers size={13} />
                  </div>
                </div>
                <div className="d-flex align-items-baseline gap-1">
                  <h5 className="fw-bold mb-0 text-dark">
                    {qtaOrdinataFor ?? 0}
                  </h5>
                  <span className="fs-8 text-muted">pz</span>
                </div>
              </div>
            </Col>

            <Col>
              <div className="p-2 bg-light rounded-3 border position-relative overflow-hidden kpi-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-warning-emphasis fw-bold fs-8 text-uppercase tracking-wider">
                    Da Evadere
                  </span>
                  <div className="kpi-icon-wrapper bg-warning-subtle text-warning">
                    <Clock size={13} />
                  </div>
                </div>
                <div className="d-flex align-items-baseline gap-1">
                  <h5 className="fw-bold mb-0 text-warning">
                    {qtaDaEvadereFor ?? 0}
                  </h5>
                  <span className="fs-8 text-warning-emphasis">pz</span>
                </div>
              </div>
            </Col>

            <Col>
              <div className="p-2 bg-light rounded-3 border position-relative overflow-hidden kpi-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-success-emphasis fw-bold fs-8 text-uppercase tracking-wider">
                    Evaso
                  </span>
                  <div className="kpi-icon-wrapper bg-success-subtle text-success">
                    <CheckCircle size={13} />
                  </div>
                </div>
                <div className="d-flex align-items-baseline gap-1">
                  <h5 className="fw-bold mb-0 text-success">
                    {qtaEvasoFor ?? 0}
                  </h5>
                  <span className="fs-8 text-success-emphasis">pz</span>
                </div>
              </div>
            </Col>

            <Col>
              <div className="p-2 bg-light rounded-3 border position-relative overflow-hidden kpi-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-info-emphasis fw-bold fs-8 text-uppercase tracking-wider">
                    Rimesso
                  </span>
                  <div className="kpi-icon-wrapper bg-info-subtle text-info">
                    <Truck size={13} />
                  </div>
                </div>
                <div className="d-flex align-items-baseline gap-1">
                  <h5 className="fw-bold mb-0 text-info">
                    {qtaRimessaFor ?? 0}
                  </h5>
                  <span className="fs-8 text-info-emphasis">pz</span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  </Modal.Body>
</Modal>    </div>
  );
};

export default SalesChart;